import { z } from 'zod';
import {
  AR_CASH_QUERY_MAX_RANGE_DAYS,
  CashDaySchema,
  cashRangeDayCount,
  type CashRange,
} from '@erp/rpc';

/** The page's range presets, in days. */
export const CASH_DASHBOARD_PRESETS = [7, 14, 30, 90] as const;
export type CashDashboardPreset = (typeof CASH_DASHBOARD_PRESETS)[number];
export const DEFAULT_CASH_DASHBOARD_DAYS: CashDashboardPreset = 14;

// An impossible date (2026-02-30) falls back to the default preset.
const DaySchema = CashDaySchema;

/**
 * The page range in the URL: `days` for a preset (the last N Pacific days),
 * or `from` and `to` for custom dates. Anything unreadable falls back to the
 * default preset.
 */
export const CashDashboardSearchSchema = z.object({
  days: z
    .union(CASH_DASHBOARD_PRESETS.map((days) => z.literal(days)))
    .optional()
    .catch(undefined),
  from: DaySchema.optional().catch(undefined),
  to: DaySchema.optional().catch(undefined),
});
export type CashDashboardSearch = z.infer<typeof CashDashboardSearchSchema>;

/** Why custom dates can't be used, or null when they can. */
export function customRangeError(
  from: string,
  to: string,
): 'order' | 'too-long' | null {
  if (from > to) return 'order';
  if (cashRangeDayCount(from, to) > AR_CASH_QUERY_MAX_RANGE_DAYS)
    return 'too-long';
  return null;
}

/** The range the URL asks for. */
export function searchRange(search: CashDashboardSearch): CashRange {
  if (search.from && search.to && !customRangeError(search.from, search.to))
    return { kind: 'between', from: search.from, to: search.to };
  return {
    kind: 'last_days',
    days: search.days ?? DEFAULT_CASH_DASHBOARD_DAYS,
  };
}
